import {Component, ElementRef} from '@angular/core';
import {NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router} from '@angular/router';
import {Observable, of} from 'rxjs';
import {debounceTime, filter} from 'rxjs/operators';
import {FIXED_VALUES_OBS as UNION_VALS, UnionInitialValues} from './union/tokens';
import {FIXED_VALUES_OBS as NON_UN_VALS, NonUnionInitialValues} from './non-union/tokens';
import {FIXED_VALUES_OBS as IMPORT_VALS, ImportInitialValues} from './import/tokens';
import {FIXED_VALUES_OBS as INT_VALS, IntermediaryInitialValues} from './intermediary/tokens';

/**
 * "Launcher/Boostrap" component for import-form
 * Receives initialValues directly from the DOM.
 **/
@Component({
  selector: 'app-root',
  templateUrl: './registo-forms-root.component.html',
  styleUrls: ['./registo-forms-root.component.scss'],
  providers: [
    {provide: UNION_VALS, useFactory: (comp: RegistoFormsRootComponent) => comp.unionInitialValues, deps: [RegistoFormsRootComponent]},
    {provide: NON_UN_VALS, useFactory: (comp: RegistoFormsRootComponent) => comp.nonUnionInitialValues, deps: [RegistoFormsRootComponent]},
    {provide: IMPORT_VALS, useFactory: (comp: RegistoFormsRootComponent) => comp.importInitialValues, deps: [RegistoFormsRootComponent]},
    {provide: INT_VALS, useFactory: (comp: RegistoFormsRootComponent) => comp.intermediaryInitialValues, deps: [RegistoFormsRootComponent]},
  ]
})
export class RegistoFormsRootComponent {

  /*spinner*/
  public loading: boolean = true;

  /** unprocessed initial values */
  public unionInitialValues: Observable<UnionInitialValues | undefined>;
  public nonUnionInitialValues: Observable<NonUnionInitialValues | undefined>;
  public importInitialValues: Observable<ImportInitialValues | undefined>;
  public intermediaryInitialValues: Observable<IntermediaryInitialValues | undefined>;

  constructor(private elementRef: ElementRef,
              private router: Router) {
    this.setupLoading();
    const registrationType = elementRef.nativeElement.getAttribute('registrationType');
    switch (registrationType) {
      case 'UNION':
        this.loadUnion(elementRef);
        setTimeout(() => this.router.navigate(['union'], {skipLocationChange: true}));
        break;
      case 'NON_UNION':
        this.loadNonUnion(elementRef);
        setTimeout(() => this.router.navigate(['non-union'], {skipLocationChange: true}));
        break;
      case 'IMPORT':
        this.loadImport(elementRef);
        setTimeout(() => this.router.navigate(['import'], {skipLocationChange: true}));
        break;
      case 'INTERMEDIARY':
        this.loadIntermediary(elementRef);
        setTimeout(() => this.router.navigate(['intermediary'], {skipLocationChange: true}));
        break;
      default:
        throw new Error(`Unknown registrationType '${registrationType}'`);
    }
  }

  private loadUnion(elementRef: ElementRef) {
    const initialValuesStr = elementRef.nativeElement.getAttribute('unionInitialValues');
    if (initialValuesStr) this.unionInitialValues = of(JSON.parse(initialValuesStr));
    else this.unionInitialValues = of({
      isSupplyCommencementEditable: false,
      isPreRegistration: false,
      loadedPrevRegistration: true,
      hasCadastroData: true,
      isNonResident: false,
      mode: {detail: true, new: false, edit: false},
      preFilledForm: undefined
    });
  }

  private loadNonUnion(elementRef: ElementRef) {
    const initialValuesStr = elementRef.nativeElement.getAttribute('nonUnionInitialValues');
    if (initialValuesStr) this.nonUnionInitialValues = of(JSON.parse(initialValuesStr));
    else this.nonUnionInitialValues = of({
      isLoggedIn: true,
      isSupplyCommencementEditable: false,
      isPreRegistration: false,
      loadedPrevRegistration: true,
      mode: {detail: true, new: false, edit: false},
      preFilledForm: undefined
    });
  }

  private loadImport(elementRef: ElementRef) {
    const initialValuesStr = elementRef.nativeElement.getAttribute('importInitialValues');
    if (initialValuesStr) this.importInitialValues = of(JSON.parse(initialValuesStr));
    else this.importInitialValues = of({
      hasCadastroData: true,
      isSupplyCommencementEditable: false,
      isPreRegistration: false,
      loadedPrevRegistration: true,
      isNonResident: false,
      registeredBy: 'NETP',
      mode: {detail: true, new: false, edit: false},
      preFilledForm: undefined
    });
  }

  private loadIntermediary(elementRef: ElementRef) {
    const initialValuesStr = elementRef.nativeElement.getAttribute('intermediaryInitialValues');
    if (initialValuesStr) this.intermediaryInitialValues = of(JSON.parse(initialValuesStr));
    else this.intermediaryInitialValues = of({
      isSupplyCommencementEditable: false,
      isPreRegistration: false,
      loadedPrevRegistration: true,
      hasCadastroData: true,
      isNonResident: false,
      mode: {detail: true, new: false, edit: false},
      preFilledForm: undefined
    });
  }

  private setupLoading() {
    this.loading = true;

    this.router.events.pipe(
      filter(evt => evt instanceof NavigationStart
        || evt instanceof NavigationEnd
        || evt instanceof NavigationCancel
        || evt instanceof NavigationError),
      debounceTime(100)

    ).subscribe((evt) => {
      if (evt instanceof NavigationStart) {
        this.loading = true;
      }
      if (evt instanceof NavigationEnd
          || evt instanceof NavigationCancel
          || evt instanceof NavigationError) {
        this.loading = false;
      }
    })
  }
}
